/**
 * KeyboardShortcuts — press ? anywhere to open the shortcut cheatsheet.
 *
 * Also wires the vim-style `g <letter>` navigation shortcuts and blocks
 * the intercept while the user is typing into an input, textarea or a
 * contenteditable region (so we never clobber real content).
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Keyboard, X } from "lucide-react";

const GROUPS = [
  {
    label: "General",
    rows: [
      { keys: ["?"], desc: "Show / hide this cheatsheet" },
      { keys: ["Esc"], desc: "Close dialogs & this sheet" },
    ],
  },
  {
    label: "Navigate (g then…)",
    rows: [
      { keys: ["g", "o"], desc: "Go to Overview" },
      { keys: ["g", "c"], desc: "Ask AI (chat)" },
      { keys: ["g", "m"], desc: "Individual memory" },
      { keys: ["g", "p"], desc: "People directory" },
      { keys: ["g", "k"], desc: "Knowledge" },
      { keys: ["g", "s"], desc: "Sources" },
      { keys: ["g", "i"], desc: "Integrations" },
      { keys: ["g", "b"], desc: "Billing" },
    ],
  },
  {
    label: "Chat",
    rows: [
      { keys: ["/"], desc: "Start a /mention on a teammate's memory" },
      { keys: ["↑", "↓"], desc: "Move between mention suggestions" },
      { keys: ["Enter"], desc: "Send message / accept mention" },
    ],
  },
];

const NAV_MAP = {
  o: "/app",
  c: "/app/chat",
  m: "/app/memory",
  p: "/app/people",
  k: "/app/knowledge",
  s: "/app/sources",
  i: "/app/integrations",
  b: "/app/billing",
};

const isTypingIn = (el) => {
  if (!el) return false;
  const tag = (el.tagName || "").toLowerCase();
  return tag === "input" || tag === "textarea" || tag === "select" || el.isContentEditable;
};

export default function KeyboardShortcuts() {
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    let awaitingGKey = false;
    let gTimer = null;

    const onKey = (e) => {
      if (isTypingIn(e.target)) return;

      // Toggle sheet with ? (Shift + /).
      if (e.key === "?" || (e.key === "/" && e.shiftKey)) {
        e.preventDefault();
        setOpen((v) => !v);
        return;
      }
      if (e.key === "Escape" && open) {
        setOpen(false);
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      // Vim-style `g x` navigation.
      if (awaitingGKey) {
        const dest = NAV_MAP[e.key];
        awaitingGKey = false;
        if (gTimer) { clearTimeout(gTimer); gTimer = null; }
        if (dest) {
          e.preventDefault();
          nav(dest);
        }
        return;
      }
      if (e.key === "g") {
        awaitingGKey = true;
        gTimer = setTimeout(() => { awaitingGKey = false; gTimer = null; }, 1500);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      if (gTimer) clearTimeout(gTimer);
    };
  }, [nav, open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] bg-background/85 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="shortcuts-modal"
      onClick={() => setOpen(false)}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl rounded-xl border border-border/60 bg-popover shadow-2xl overflow-hidden"
      >
        <header className="flex items-start justify-between gap-4 px-6 py-5 border-b border-border/60">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground">Cheatsheet</p>
            <h2 className="font-display text-xl mt-1 flex items-center gap-2">
              <Keyboard className="w-4 h-4" /> Keyboard shortcuts
            </h2>
            <p className="text-xs text-muted-foreground mt-1">
              Move around Ragnes without leaving the keyboard.
            </p>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="text-muted-foreground hover:text-foreground rounded-full p-1"
            data-testid="shortcuts-close-btn"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="px-6 py-5 grid gap-6 sm:grid-cols-2 max-h-[70vh] overflow-auto">
          {GROUPS.map((g) => (
            <div key={g.label} className="space-y-3">
              <p className="text-[10px] uppercase tracking-[0.28em] text-sky-500">{g.label}</p>
              <ul className="space-y-2">
                {g.rows.map((row) => (
                  <li key={row.desc} className="flex items-center justify-between gap-3">
                    <span className="text-sm text-muted-foreground">{row.desc}</span>
                    <span className="flex items-center gap-1 shrink-0">
                      {row.keys.map((k, i) => (
                        <kbd
                          key={i}
                          className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-border/80 bg-muted text-foreground min-w-[22px] text-center"
                        >
                          {k}
                        </kbd>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <footer className="px-6 py-3 border-t border-border/60 text-[11px] text-muted-foreground flex items-center justify-between">
          <span>
            Press{" "}
            <kbd className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-border/80 bg-muted">?</kbd>{" "}
            anywhere to reopen
          </span>
          <span className="font-mono">Ragnes</span>
        </footer>
      </div>
    </div>
  );
}
